import { useState } from 'react';
import { useToast } from 'src/components/useToast';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
  SheetTrigger,
} from '@/components/ui/sheet';
import { InvoiceItem } from '@libs/schemas/src/invoice';
import { Invoice } from 'src/trpc';
import { InvoiceItemEditor } from './InvoiceItemEditor';
import { Button } from '@/components/ui/button';
import { LucidePencil, Plus } from 'lucide-react';

export const InvoiceSelectItemsSheet = ({
  invoice,
  isLoading,
  onSelectItems,
}: {
  invoice: Invoice;
  isLoading?: boolean;
  onSelectItems: (items: InvoiceItem[]) => void;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [editedItems, setEditedItems] = useState<InvoiceItem[]>(invoice.items);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const { errorToast } = useToast();
  if (!invoice) return null;

  const handleRemoveItem = (index: number) => {
    const updatedItems = editedItems.filter((_, i: number) => i !== index);
    setEditedItems(updatedItems);
  };

  const handleSaveChanges = () => {
    // Validate that all items have names
    const emptyItems = editedItems.filter((item) => !item.label || item.label.trim() === '');
    if (emptyItems.length > 0) {
      setShowValidationErrors(true);
      errorToast({
        title: 'Validation Error',
        description: 'Please fill in all product names before saving.',
      });
      return;
    }

    setShowValidationErrors(false);
    setOpen(false);
    onSelectItems(editedItems);
  };

  const handleAddItem = () => {
    const newItem: InvoiceItem = {
      label: '',
      description: '',
      quantity: 1,
      unitPriceInCents: 0,
      totalPriceInCents: 0,
      totalTaxAmountInCents: 0,
      collectTaxes: false,
    };
    setEditedItems([...editedItems, newItem]);
    // The key for the new item will be generated automatically when getItemKey is called
  };

  const handleUpdateItem = (
    index: number,
    changes: Record<string, number | string | boolean | undefined>,
  ) => {
    const updatedItems = [...editedItems];
    updatedItems[index] = { ...updatedItems[index], ...changes };
    if (changes.quantity || changes.unitPriceInCents) {
      const quantity = updatedItems[index].quantity;
      const unitPrice = updatedItems[index].unitPriceInCents;
      updatedItems[index]['totalPriceInCents'] = quantity * unitPrice;
    }

    // Clear validation errors if user is fixing the label
    if (
      changes.label &&
      typeof changes.label === 'string' &&
      changes.label.trim() !== '' &&
      showValidationErrors
    ) {
      setShowValidationErrors(false);
    }

    setEditedItems(updatedItems);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          className="absolute top-0 right-0"
          variant="ghost"
          onClick={() => setOpen(true)}
        >
          <LucidePencil className="w-4 h-4" />
        </Button>
      </SheetTrigger>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-lg font-semibold text-gray-900">
              Invoice Items
            </SheetTitle>
          </SheetHeader>

          <div className="overflow-y-auto px-6 py-6">
            <div className="space-y-4">
              {editedItems.map((item: InvoiceItem, index: number) => (
                <div key={index}>
                  <InvoiceItemEditor
                    item={item}
                    index={index}
                    onUpdate={handleUpdateItem}
                    onRemove={handleRemoveItem}
                    showValidationError={showValidationErrors}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button variant="outline" size="sm" onClick={handleAddItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>

          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full justify-end gap-4">
              <SheetClose asChild>
                <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
              </SheetClose>
              <Button variant="default" size="sm" disabled={isLoading} onClick={handleSaveChanges}>
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
